import { useContext } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import type { CacheRequestConfig } from 'axios-cache-interceptor';

import { ConfigContext } from '../../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../../aurr/contexts/LoginContext';
import { buildCacheKey, getAxiosInstance } from '../../../axiosCache';
import {
  REPLIES_MUTATE_POST_KEY,
  REPLIES_MUTATE_UPDATE_KEY,
  REPLIES_QUERY_KEY,
} from '../../constants';
import { KhorosRepliesProps } from '../../interface/khoros.interface';
import { KhorosRepliesGetSchema } from '../../type/khoros-replies.type';

export const useKhorosEditPost = ({
  topicId,
  commentId,
  baseURL,
}: KhorosRepliesProps) => {
  const { KHOROS_API } = useContext(ConfigContext);
  const { access_token, userProfile } = useContext(LoginContext);
  const sub = userProfile?.sub;

  const axiosConfig: CacheRequestConfig = {
    baseURL: baseURL || KHOROS_API,
    headers: {
      ...(access_token && { authorization: `Bearer ${access_token}` }),
    },
    cache: {
      update: {
        [buildCacheKey([REPLIES_QUERY_KEY, topicId])]: 'delete',
      },
    },
  };

  const api = getAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [REPLIES_MUTATE_UPDATE_KEY, { topicId, sub }],
    mutationFn: async body => {
      const response = await api.put(
        `/services/v2/community/khoros/replies?id=${commentId}`,
        {
          body,
        },
        axiosConfig
      );
      return response?.data || undefined;
    },
    onMutate: async (body: string) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [REPLIES_QUERY_KEY, { topicId }],
      });
      await queryClient.cancelQueries({
        queryKey: [REPLIES_MUTATE_POST_KEY, { topicId, sub }],
      });

      // Snapshot the previous value
      const savedReplies = queryClient.getQueryData<KhorosRepliesGetSchema>([
        REPLIES_QUERY_KEY,
        { topicId },
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData<InfiniteData<KhorosRepliesGetSchema>>(
        [REPLIES_QUERY_KEY, { topicId }],
        old => {
          const newResponse = {
            pages: [...(old?.pages || [])],
            pageParams: old?.pageParams || [{ 0: undefined }],
          };

          let commentIndex = -1;
          const pageIndex = newResponse.pages.findIndex(page => {
            const foundCommentIndex = page.kh_replies.findIndex(
              obj => obj.kh_post_id === commentId
            );
            if (foundCommentIndex > -1) {
              commentIndex = foundCommentIndex;
              return true;
            }
            return false;
          });

          if (pageIndex === -1 || commentIndex === -1) {
            // eslint-disable-next-line no-console
            console.error(
              '[AURR Comments] - Attempted to edit a comment that does not exist.'
            );
            return newResponse;
          }

          newResponse.pages[pageIndex].kh_replies[commentIndex] = {
            ...newResponse.pages[pageIndex].kh_replies[commentIndex],
            kh_body: body,
            isOptimistic: true,
          };

          return newResponse;
        }
      );
      return { savedReplies };
    },
    onError: (err, newReply, context) => {
      queryClient.setQueryData(
        [REPLIES_QUERY_KEY, { topicId }],
        context?.savedReplies
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [REPLIES_QUERY_KEY, { topicId }],
      });
    },
  });
};
